import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../utilis/api";
import { online, url } from "../../utilis/contant";
import "./pages.css";

const LESSON_PRICES = { 60: 120, 30: 84 };
const DISCOUNT_RATES = { monthly: 0.1, yearly: 0.2 };
const PLAN_LABELS = {
  monthly: "Base Plan — 10% off",
  yearly: "VIP Plan — 20% off",
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const generateTimeSlots = (start, end, durationMinutes) => {
  const slots = [];
  const [sH, sM] = start.split(":").map(Number);
  const [eH, eM] = end.split(":").map(Number);
  const startTotal = sH * 60 + sM;
  const endTotal = eH * 60 + eM;
  for (
    let t = startTotal;
    t + durationMinutes <= endTotal;
    t += durationMinutes
  ) {
    const h = String(Math.floor(t / 60)).padStart(2, "0");
    const m = String(t % 60).padStart(2, "0");
    slots.push(`${h}:${m}`);
  }
  return slots;
};

const getAvailableDates = (availability) => {
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dayName = DAY_NAMES[d.getDay()];
    const dayData = availability?.find((a) => a.day === dayName && a.isActive);
    if (dayData) dates.push({ date: d, dayData });
  }
  return dates;
};
const convertToUserTimezone = (time, offset) => {
  const [h, m] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(h);
  date.setMinutes(m);

  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const target = utc + parseInt(offset) * 3600000;

  const final = new Date(target);

  return final.toTimeString().slice(0, 5);
};
const formatDate = (date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const toDateString = (date) => date.toISOString().split("T")[0];

// ✅ Filter out past time slots for today
const filterPastSlots = (slots, selectedDate) => {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = selectedDate.getTime() === today.getTime();
  if (!isToday) return slots; // future dates — show all

  return slots.filter((slot) => {
    const [h, m] = slot.split(":").map(Number);
    const slotTime = new Date();
    slotTime.setHours(h, m, 0, 0);
    return slotTime > now; // only future slots
  });
};

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const { teacher, duration } = location.state || {};

  const [subscription, setSubscription] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(duration || "60");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!teacher) {
      navigate("/dashboard/student/teachers");
      return;
    }
    if (!token) {
      navigate("/");
      return;
    }

    const loadAll = async () => {
      try {
        const res = await api.get("/payments/my-subscription", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = res.data;

        setSubscription(data);
      } catch (err) {
        setSubscription({ hasSubscription: false });
      }
      try {
        const teacherRes = await api.get(
          `/teacher/${teacher.userId?._id || teacher._id}`,
        );
        setAvailability(teacherRes.data?.data?.availability || []);
        // console.log(teacherRes);
        // console.log(teacherRes.data?.data?.availability);
      } catch {
        setAvailability([]);
      }

      setLoadingData(false);
    };

    loadAll();
  }, [token, teacher, navigate]);

  // ✅ Fetch booked slots when date changes
  useEffect(() => {
    setSelectedTime(null);
    setBookedSlots([]);
    if (!selectedDate || !teacher) return;

    const fetchBookedSlots = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = toDateString(selectedDate.date);
        const res = await api.get(
          `/booking/booked-slots?teacherId=${teacher._id}&date=${dateStr}`,
        );
        const times = (res.data?.bookedTimes || []).map((t) => t.trim());
        setBookedSlots(times);
      } catch {
        setBookedSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchBookedSlots();
  }, [selectedDate, teacher]);

  useEffect(() => {
    console.log("📦 Selected:", {
      date: selectedDate ? toDateString(selectedDate.date) : null,
      time: selectedTime,
    });
  }, [selectedDate, selectedTime]);

  if (!teacher) return null;

  const isActiveSub =
    subscription?.hasSubscription === true && subscription?.status === "Active";
  const planType = isActiveSub ? subscription.planType : null;
  const discountRate = planType ? DISCOUNT_RATES[planType] || 0 : 0;
  const originalPrice = LESSON_PRICES[selectedDuration] || 120;
  const discountAmount = Math.round(originalPrice * discountRate * 100) / 100;
  const finalPrice = originalPrice - discountAmount;

  const availableDates = getAvailableDates(availability);

  // ✅ Generate slots then filter past ones for today
  // console.log(selectedDuration);
  const rawTimeSlots = selectedDate
    ? (() => {
        const daySlots = selectedDate.dayData?.slots || [];

        let allSlots = [];

        daySlots.forEach((slot) => {
          const generated = generateTimeSlots(
            slot.start,
            slot.end,
            parseInt(selectedDuration),
          );

          allSlots = [...allSlots, ...generated];
        });
        // console.log(allSlots);
        return allSlots;
      })()
    : [];

  const timeSlots = selectedDate
    ? filterPastSlots(rawTimeSlots, selectedDate.date)
    : [];
  const to12Hour = (time) => {
    let [h, m] = time.split(":").map(Number);

    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;

    return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
  };
  const handlePay = async () => {
    console.log("🟡 handlePay clicked");

    // 🚫 Prevent double click
    if (paying) {
      console.log("⛔ Already processing payment - blocked");
      return;
    }

    setError("");

    if (!selectedDate) {
      setError("Please select a date.");
      return;
    }

    if (!selectedTime) {
      setError("Please select a time slot.");
      return;
    }

    // 🚨 EXTRA SAFETY (frontend duplicate check)
    if (bookedSlots.includes(selectedTime)) {
      console.log("❌ Slot already booked (frontend check)");
      setError("This slot was just booked. Please choose another.");
      return;
    }

    setPaying(true);

    try {
      console.log("🚀 Sending booking request...");
      console.log({
        teacherId: teacher._id,
        date: toDateString(selectedDate.date),
        time: selectedTime,
        duration: selectedDuration,
      });

      const res = await api.post(
        "/payments/create-lesson-checkout",
        {
          teacherId: teacher._id,
          teacherName: teacher.userId?.name || teacher.name,
          duration: parseInt(selectedDuration),
          finalPrice,
          originalPrice,
          discountRate,
          date: toDateString(selectedDate.date),
          time: selectedTime,
          studentId: user._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = res.data;

      console.log("✅ API Response:", data);

      if (data.url) {
        console.log("➡️ Redirecting to Stripe...");
        window.location.href = data.url;
      } else {
        console.log("❌ No URL returned");
        setError(data.message || "Could not start payment.");
      }
    } catch (err) {
      console.log("🔥 Payment error:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setPaying(false);
    }
  };

  if (loadingData) {
    return (
      <div className="overview-container">
        <p style={{ color: "#888", textAlign: "center" }}>
          Loading booking details...
        </p>
      </div>
    );
  }

  const canProceed = selectedDate && selectedTime;
  const formatTimezone = (offset) => {
    const map = {
      "-05:00": "Eastern Time (EST)",
      "-06:00": "Central Time (CST)",
      "-07:00": "Mountain Time (MST)",
      "-08:00": "Pacific Time (PST)",
      "-10:00": "Hawaii Time (HST)",
    };

    return map[offset] || `UTC ${offset}`;
  };

  return (
    <div className="overview-container">
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "none",
          border: "none",
          color: "#9ca3af",
          fontSize: "14px",
          cursor: "pointer",
          marginBottom: "1.5rem",
          padding: 0,
        }}
      >
        ← Back to Teachers
      </button>
      <h2 style={{ marginBottom: "1.5rem" }}>Book a Lesson</h2>
      {/* Teacher Card */}
      <div
        style={{
          background: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "1.2rem 1.5rem",
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {teacher.userId?.image ? (
          <img
            src={`${url}/${teacher.userId.image}`}
            alt={teacher.userId?.name}
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "#e6be5d22",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.4rem",
              fontWeight: 700,
              color: "#e6be5d",
              flexShrink: 0,
            }}
          >
            {(teacher.userId?.name || "T").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <p
            style={{
              color: "#fff",
              fontWeight: 600,
              margin: 0,
              fontSize: "15px",
            }}
          >
            {teacher.userId?.name || teacher.name}
          </p>
          <p style={{ color: "#9ca3af", fontSize: "13px", margin: "2px 0 0" }}>
            {teacher.skills}
          </p>
          {teacher.bio && (
            <p
              style={{ color: "#6b7280", fontSize: "12px", margin: "4px 0 0" }}
            >
              {teacher.bio}
            </p>
          )}
        </div>
      </div>
      {/* Step 1 Duration */}
      <div style={{ marginBottom: "1.5rem" }}>
        <p
          style={{
            color: "#9ca3af",
            fontSize: "13px",
            marginBottom: "8px",
            fontWeight: 600,
          }}
        >
          1. Select lesson duration
        </p>
        <div style={{ display: "flex", gap: "12px" }}>
          {[
            { value: "60", label: "1 Hour", price: "$120" },
            { value: "30", label: "30 Minutes", price: "$84" },
          ].map((opt) => {
            const isSel = selectedDuration === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelectedDuration(opt.value)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: isSel ? "2px solid #e6be5d" : "1px solid #333",
                  background: isSel ? "#1a1200" : "#111",
                  color: isSel ? "#e6be5d" : "#9ca3af",
                  cursor: "pointer",
                  fontWeight: isSel ? 600 : 400,
                  fontSize: "14px",
                  transition: "all 0.15s",
                }}
              >
                <div>{opt.label}</div>
                <div
                  style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7 }}
                >
                  from {opt.price}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Step 2 Date */}
      <div style={{ marginBottom: "1.5rem" }}>
        <p
          style={{
            color: "#9ca3af",
            fontSize: "13px",
            marginBottom: "8px",
            fontWeight: 600,
          }}
        >
          2. Select a date
        </p>
        {availableDates.length === 0 ? (
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: "10px",
              padding: "16px",
              color: "#6b7280",
              fontSize: "13px",
              textAlign: "center",
            }}
          >
            ⚠️ This teacher has no available slots in the next 14 days.
          </div>
        ) : (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {availableDates.map(({ date, dayData }, i) => {
              const isSel =
                selectedDate &&
                toDateString(selectedDate.date) === toDateString(date);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate({ date, dayData })}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: isSel ? "2px solid #e6be5d" : "1px solid #333",
                    background: isSel ? "#1a1200" : "#111",
                    color: isSel ? "#e6be5d" : "#9ca3af",
                    fontWeight: isSel ? 600 : 400,
                    fontSize: "13px",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDate(date)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* step 3 */}
      {selectedDate && (
        <div style={{ marginBottom: "1.5rem" }}>
          <p
            style={{
              color: "#9ca3af",
              fontSize: "13px",
              marginBottom: "8px",
              fontWeight: 600,
            }}
          >
            Teacher Timezone
          </p>
          <p style={{ color: "#6b7280", fontSize: "13px" }}>
            {formatTimezone(selectedDate.dayData?.timezone)}
          </p>
        </div>
      )}
      {/* Step 3 Time */}
      {selectedDate && (
        <div style={{ marginBottom: "1.5rem" }}>
          <p
            style={{
              color: "#9ca3af",
              fontSize: "13px",
              marginBottom: "8px",
              fontWeight: 600,
            }}
          >
            3. Select a time slot
          </p>
          {loadingSlots ? (
            <p style={{ color: "#6b7280", fontSize: "13px" }}>
              Loading available times...
            </p>
          ) : timeSlots.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: "13px" }}>
              No available slots for this day.
            </p>
          ) : (
            <>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {timeSlots.map((slot) => {
                  const isBooked = bookedSlots.includes(slot.trim());
                  const isSel = selectedTime === slot;
                  return (
                    <button
                      key={slot}
                      disabled={isBooked}
                      onClick={() => !isBooked && setSelectedTime(slot)}
                      style={{
                        minWidth: "95px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: isBooked
                          ? "1px solid #2a2a2a"
                          : isSel
                            ? "2px solid #22c55e"
                            : "1px solid #333",
                        background: isBooked
                          ? "#1a1a1a"
                          : isSel
                            ? "#052e16"
                            : "#111",
                        color: isBooked
                          ? "#444"
                          : isSel
                            ? "#22c55e"
                            : "#d1d5db",
                        cursor: isBooked ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                        position: "relative",
                        transform: isSel ? "translateY(-2px)" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {/* 🔥 Main Time */}
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            letterSpacing: "0.3px",
                          }}
                        >
                          {to12Hour(slot)}
                        </span>

                        {/* 🌍 Timezone Badge */}
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            background: isSel ? "#14532d" : "#222",
                            color: isSel ? "#22c55e" : "#aaa",
                            border: isSel
                              ? "1px solid #22c55e55"
                              : "1px solid #333",
                          }}
                        >
                          {formatTimezone(selectedDate.dayData?.timezone)}
                        </span>
                      </div>

                      {/* ❌ Booked Label */}
                      {isBooked && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: "4px",
                            right: "6px",
                            fontSize: "9px",
                            color: "#666",
                          }}
                        >
                          Booked
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {bookedSlots.length > 0 && (
                <p
                  style={{
                    color: "#4b5563",
                    fontSize: "11px",
                    marginTop: "8px",
                  }}
                >
                  ⬛ Greyed slots are already booked
                </p>
              )}
            </>
          )}
        </div>
      )}
      {/* Price Breakdown */}
      <div
        style={{
          background: "#111",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "1.2rem 1.5rem",
          marginBottom: "1.5rem",
        }}
      >
        <p
          style={{
            color: "#9ca3af",
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "1rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Price Breakdown
        </p>
        <div style={{ marginBottom: "1rem" }}>
          {planType ? (
            <span
              style={{
                background: planType === "yearly" ? "#e6be5d22" : "#6366f122",
                border: `1px solid ${planType === "yearly" ? "#e6be5d55" : "#6366f155"}`,
                color: planType === "yearly" ? "#e6be5d" : "#818cf8",
                fontSize: "12px",
                padding: "3px 12px",
                borderRadius: "20px",
                fontWeight: 600,
              }}
            >
              {planType === "yearly" ? "👑 " : "✓ "}
              {PLAN_LABELS[planType]}
            </span>
          ) : (
            <span
              style={{
                background: "#33333322",
                border: "1px solid #444",
                color: "#888",
                fontSize: "12px",
                padding: "3px 12px",
                borderRadius: "20px",
                fontWeight: 600,
              }}
            >
              No Subscription
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "14px",
            }}
          >
            <span style={{ color: "#9ca3af" }}>
              {selectedDuration === "60" ? "1 Hour Lesson" : "30 Min Lesson"}
            </span>
            <span style={{ color: "#fff" }}>${originalPrice}</span>
          </div>
          {discountRate > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "14px",
              }}
            >
              <span style={{ color: "#22c55e" }}>
                Subscription Discount ({Math.round(discountRate * 100)}% off)
              </span>
              <span style={{ color: "#22c55e" }}>
                - ${discountAmount.toFixed(2)}
              </span>
            </div>
          )}
          <div
            style={{
              borderTop: "1px solid #333",
              paddingTop: "10px",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            <span style={{ color: "#fff" }}>Total</span>
            <span style={{ color: "#e6be5d" }}>${finalPrice.toFixed(2)}</span>
          </div>
        </div>
        {!planType && (
          <div
            style={{
              marginTop: "1rem",
              padding: "10px 12px",
              background: "#1a1200",
              border: "1px solid #e6be5d44",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#9ca3af",
            }}
          >
            💡 Subscribe to save up to 20% on every lesson.{" "}
            <span
              style={{ color: "#e6be5d", cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/membership")}
            >
              View Plans →
            </span>
          </div>
        )}
      </div>
      {error && (
        <div
          style={{
            background: "#1a0000",
            border: "1px solid #ef444455",
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "1rem",
            color: "#ef4444",
            fontSize: "13px",
          }}
        >
          ⚠️ {error}
        </div>
      )}
      <button
        className="dashboard-btn register-btn"
        onClick={handlePay}
        disabled={paying || !canProceed}
        style={{
          width: "100%",
          padding: "14px",
          fontSize: "15px",
          fontWeight: 600,
          opacity: !canProceed ? 0.5 : 1,
          cursor: !canProceed ? "not-allowed" : "pointer",
        }}
      >
        {paying
          ? "Redirecting to payment..."
          : canProceed
            ? `Pay $${finalPrice.toFixed(2)} — Confirm Booking`
            : "Select a date and time to continue"}
      </button>
      <p
        style={{
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        🔒 Secure payment via Stripe. Your Zoom meeting is created after
        payment.
      </p>
    </div>
  );
};

export default BookingConfirmation;
